<template>
  <div
    class="flex h-13"
    @mouseover="hoverState = true"
    @mouseleave="hoverState = false"
  >
    <div class="sticky left-0 z-10 flex pl-6 bg-pure-white">
      <div
        class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b border-l w-14 border-slate-400"
        :class="[
          isLast ? 'rounded-bl' : '',
          hoverState ? 'bg-hover-state' : 'bg-pure-white'
        ]"
      >
        <BlInputCheck
          :checked="checked"
          @changed="$emit('change-selected')"
          :title="
            report.status !== 'published'
              ? 'You can only download reports that have been published'
              : ''
          "
        />
      </div>
      <div
        class="flex items-center flex-shrink-0 gap-2 px-4 transition-all duration-300 border-b border-r w-60 border-slate-400"
        :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
      >
        <BlTextBody2
          :title="report.name"
          :text="report.name"
          class="truncate text-slate-1000"
        />
      </div>
    </div>
    <div
      class="flex items-center flex-shrink-0 w-40 px-4 transition-all duration-300 border-b border-slate-400"
      :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
    >
      <BlChip :chip="report.status" dense />
    </div>
    <div
      class="flex items-center flex-shrink-0 w-56 px-4 transition-all duration-300 border-b border-slate-400"
      :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
    >
      <BlTextBody2 :title="dateRange" :text="dateRange" class="truncate" />
    </div>
    <div
      class="flex items-center flex-shrink-0 w-56 px-4 transition-all duration-300 border-b border-slate-400"
      :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
    >
      <BlTextBody2
        :title="templateType"
        :text="templateType"
        class="truncate"
      />
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-50 border-slate-400"
      :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
    >
      <BlTextBody2
        v-if="!report.user_id"
        title="Deleted user"
        text="Deleted user"
        class="italic truncate"
      />
      <div
        v-else
        :title="report.user.name"
        class="flex items-center justify-center h-8 px-3 truncate border rounded bg-success-100 border-success-100 max-w-25"
      >
        <BlTextBody1 :text="report.user.name" class="truncate text-success" />
      </div>
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-50 border-slate-400"
      :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
    >
      <BlTextBody2 :title="dateUpdated" :text="dateUpdated" class="truncate" />
    </div>
    <div
      class="flex items-center flex-shrink-0 px-4 transition-all duration-300 border-b w-50 border-slate-400"
      :class="[hoverState ? 'bg-hover-state' : 'bg-pure-white']"
    >
      <BlTextBody2
        v-if="!report.updated_by_user_id"
        text="-"
        class="italic truncate"
      />
      <BlTextBody2
        v-else
        :title="report.updated_by_user.name || 'Deleted user'"
        :text="report.updated_by_user.name || 'Deleted user'"
        class="truncate"
        :class="{ ' italic': !report.updated_by_user.name }"
      />
    </div>
    <div class="sticky right-0 z-10 flex flex-grow pr-6 bg-pure-white">
      <div
        class="flex items-center justify-end flex-grow flex-shrink-0 gap-2 px-4 transition-all duration-300 border-b border-r w-60 border-slate-400"
        :class="[
          isLast ? 'rounded-br' : '',
          hoverState ? 'bg-hover-state' : 'bg-pure-white'
        ]"
      >
        <BlInputButton
          v-if="report.status === 'published'"
          type="outlined"
          dense
          label="Download"
          :loading="downloadLoading"
          @clicked="onDownload"
        />
        <BlInputButton
          v-if="report.status === 'published'"
          type="outlined"
          dense
          label="Share"
          @clicked="shareOpened = true"
        />
        <router-link
          v-if="report.status === 'published'"
          :to="`/report/${report.slug}`"
          target="_blank"
        >
          <BlInputButton type="outlined" dense label="View" />
        </router-link>
        <router-link
          v-if="report.status === 'unpublished'"
          :to="`/clients/${client.id}/report/${report.slug}/edit`"
        >
          <BlInputButton type="outlined" dense label="Edit" />
        </router-link>
      </div>
    </div>

    <BlBackdrop
      v-if="shareOpened"
      title="Share report"
      cancelLabel="Close"
      @backdrop-close="shareOpened = false"
    >
      <div class="px-4 lg:px-8 lg:w-160">
        <div class="mb-3">
          <BlTextBody2 text="Share" />
          <BlTextBody2 :text="report.name" class="font-bold" />
          <BlTextBody2
            text="with your clients by copying the link or saving the QR code below."
          />
        </div>
        <div
          class="relative flex items-end gap-2 mb-3 cursor-pointer"
          @click="copyToClipboard"
        >
          <BlInputText :input="linkInput" class="pr-8" />
          <div class="absolute right-1">
            <BlInputButton icon="copy" type="plain" />
          </div>
        </div>
        <div class="flex flex-col items-center justify-center gap-3">
          <div :id="`qr-code-${report.id}`" class="p-4">
            <QrcodeVue :value="linkInput.model" :size="200" level="L" />
          </div>
        </div>
      </div>
    </BlBackdrop>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import QrcodeVue from "qrcode.vue";
import { getDateWithoutTimezone, displayDate } from "@/plugins/helper.js";

import BlInputButton from "../../BlInputButton.vue";
import BlInputText from "../../BlInputText.vue";
import BlInputCheck from "../../BlInputCheck.vue";
import BlTextBody1 from "../../BlTextBody1.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlChip from "../../BlChip.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "ClientReportItem",
  components: {
    QrcodeVue,
    BlInputButton,
    BlInputText,
    BlInputCheck,
    BlTextBody1,
    BlTextBody2,
    BlChip,
    BlBackdrop
  },
  props: {
    report: { type: Object, required: true },
    checked: { type: Boolean, default: false },
    isLast: { type: Boolean, default: false }
    // @change-selected
  },
  data() {
    return {
      hoverState: false,
      //
      downloadLoading: false,
      shareOpened: false,
      linkInput: {
        placeholder: "Link",
        model: "",
        disabled: true,
        readonly: true
      }
    };
  },
  computed: {
    ...mapGetters(["client"]),
    dateRange() {
      const start = this.getDateWithoutTimezone(this.report.start_date);
      const end = this.getDateWithoutTimezone(this.report.end_date);
      if (!start && !end) return "-";
      if (!start) return "... - " + end;
      if (!end) return start + " - ...";
      return start + " - " + end;
    },
    templateType() {
      const options = {
        progress_highlights: "Progress highlights report",
        mastered_targets: "Mastered targets report",
        selected_curricula_or_target_types:
          "Report by selected curricula or data collection methods"
      };
      if (!this.report.template_type) return "Blank report";
      return options[this.report.template_type];
    },
    dateUpdated() {
      const date = this.report.updated_at;
      return this.displayDate({ date, format: "DD MMM YYYY hh:mm" });
    }
  },
  mounted() {
    const origin = window && window.location ? window.location.origin : "";
    this.linkInput.model = `${origin}/report/${this.report.slug}`;
  },
  methods: {
    ...mapActions(["showToast", "downloadReports"]),
    displayDate,
    getDateWithoutTimezone,
    onDownload() {
      this.downloadLoading = true;
      const data = {
        ids: [this.report.id]
      };
      this.downloadReports(data)
        .then(res => {
          const href = URL.createObjectURL(res.data);

          // generate filename
          const currentReportName = this.report ? this.report.name : "report";
          const filename = `${currentReportName}.pdf`;

          const link = document.createElement("a");
          link.href = href;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();

          document.body.removeChild(link);
          URL.revokeObjectURL(href);
          this.downloadLoading = false;
        })
        .catch(err => {
          console.error(err);
          this.downloadLoading = false;
        });
    },
    copyToClipboard() {
      if (this.linkInput.model) {
        navigator.clipboard.writeText(this.linkInput.model);
        const title = "Link has been copied to clipboard";
        this.showToast({ title, message: "", type: "success" });
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/clients/report/Item.vue